<template>
  <div>
    <panel>
      <w-table
        v-if="columns"
        entity-type="document"
        id-key="documentId"
        total-text="Liczba wniosków: "
        :hide-add-button="true"
        :actions="tableButtons"
        :columns="columns"
        :data="list"
        :show-checkboxes="true"
        :show-total="true"
        :total="count"
        :per-page="-1"
        @checkAll="checkAll"
        @delete="showDeleteDialog($event.documentId)"
        @setStatus="openStatusPicker"
        @view="goToDetails"
        @finishAssessment="finishAssessment"
      >
        <list-actions
          v-if="checkedList.length > 0"
          slot="showCheckboxesButtons"
          entity-type="document"
          :entity-ids="checkedIds"
          :actions="actions"
          :object="{ status }"
          @execute="execute"
          @setStatus="openMultipleStatusPicker"
        >
          <template slot="extraActions">
            <check-permission v-if="showCreatePack && checkedList.length > 1" :permissions="createPackPermissions">
              <w-button
                class="btn-light mr-2 mb-2"
                text="Przekaż do OW"
                icon="fal fa-envelope mr-2"
                @click="openCreatePackModal"
              />
            </check-permission>

            <check-permission v-if="documentViewId" permissions="DOCUMENT.RENDER">
              <post-form
                button-class="mr-2 btn-light mb-2"
                icon="fal fa-cloud-download mr-2"
                :text="getPdfText"
                :url="renderLink"
                :fields="renderParams"
              />
            </check-permission>

            <check-permission v-if="views && views.length" permissions="DOCUMENT.RENDER">
              <w-button
                class="mr-2 btn-light mb-2"
                text="Generuj"
                icon="fal fa-file-pdf"
                :disabled="!list.length"
                @click="openGenerateModal"
              />
            </check-permission>

            <check-permission permissions="DOCUMENT.RENDER">
              <w-button
                class="mr-2 btn-light mb-2"
                text="Pobierz dokumentacje"
                icon="fal fa-cloud-download"
                @click="openDownloadModal"
              />
            </check-permission>

            <check-permission permissions="DOCUMENT.CSV">
              <w-button
                class="btn-light mr-2 mb-2"
                text="Eksport listy do csv"
                icon="fas fa-save mr-2"
                @click="openCsvModal"
              />
            </check-permission>
          </template>
        </list-actions>

        <div slot="filters">
          <filters :available-filters="filters" :entity-id="procedureId" @filter="updateList($route, $route)" />
        </div>
      </w-table>
    </panel>

    <pick-status-modal
      :id="statusPickerDocumentId"
      :object="statusPickerDocument"
      :show="showStatusPicker"
      @update="saveStatus"
      @save="saveStatuses"
      @close="closeStatusPicker"
    />

    <schema-form-modal
      :object="actionData"
      :show="!!actionData"
      :selected-document="selectedDocument"
      :external="{ documentIndex: selectedDocument }"
      @save="sendModalData"
      @close="closeActionDataModal"
    />

    <generate-modal :show="showGenerateModal" :filters="renderParams" @close="closeGenerateModal" />

    <download-modal :show="showDownloadModal" :filters="renderParams" @close="closeDownloadModal" />

    <csv-modal :show="showCsvModal" :url="csvLink" :document-ids="checkedIds" @close="closeCsvModal" />

    <create-pack-modal :show="showCreatePackModal" @save="createPack" @close="closeCreatePackModal" />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import ListActions from '@/components/actions/ListActions';
import CreatePackModal from '@/components/modals/CreatePackModal';
import CsvModal from '@/components/modals/CsvModal';
import DownloadModal from '@/components/modals/DownloadModal';
import GenerateModal from '@/components/modals/GenerateModal';
import SchemaFormModal from '@/components/modals/SchemaFormModal';
import PostForm from '@/components/postForm/PostForm';
import Filters from '@/components/table/components/Filters';
import WTable from '@/components/table/Table';
import { Errors } from '@/enums/errors.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { StageType } from '@/enums/stageType.enum';
import { Successes } from '@/enums/successes.enum';
import { boolean } from '@/filters/boolean';
import { buttonsMixin } from '@/mixins/buttons';
import { documentManageMixin } from '@/mixins/document/documentManage';
import { listMixin } from '@/mixins/list';
import { tableMultipleMixin } from '@/mixins/tableMultiple';
import { config } from '@/utils/config';
import { DELETE_ACTION, SET_STATUS_ACTION, VIEW_ACTION } from '@/utils/constants';
import { documentIndexFilters, getDocumentListData, getFilters } from '@/utils/document';
import { tryParseInt } from '@/utils/number';

const messages = {
  listError: 'Wystąpił błąd podczas pobierania wniosków.',
  getError: 'Wystąpił błąd podczas pobierania danych wniosku.',
  added: 'Dodano wniosek.',
  addError: 'Nie udało się dodać wniosku.',
  updated: 'Zaktualizowano dane wniosku.',
  updateError: 'Nie udało się zaktualizować danych wniosku.',
  deleteConfirm: 'Czy na pewno chcesz usunąć wniosek?',
  deleted: 'Usunięto wniosek.',
  deleteError: 'Nie udało się usunąć wniosku.'
};

// @vuese
// Szczegóły dokumentu
// @group Procedure
export default {
  components: {
    CreatePackModal,
    CsvModal,
    DownloadModal,
    GenerateModal,
    SchemaFormModal,
    Filters,
    ListActions,
    PostForm,
    WTable
  },
  mixins: [
    listMixin('document', messages),
    documentManageMixin,
    tableMultipleMixin,
    buttonsMixin(StageType.DOCUMENT, [], getDocumentListData)
  ],

  data() {
    return {
      singleRowActions: [VIEW_ACTION, SET_STATUS_ACTION],
      statusPickerDocumentIds: [],
      csvLink: config.csvURL + '/documents',
      showGenerateModal: false,
      showCsvModal: false,
      showCreatePackModal: false,
      showDownloadModal: false
    };
  },

  computed: {
    procedureId() {
      return this.$route.params.procedureId.toString();
    },

    statusId() {
      return this.$route.query.statusId;
    },

    actions() {
      const actions = this.buttons.filter(button => !!button.action);
      actions.push(SET_STATUS_ACTION);
      return actions;
    },

    renderLink() {
      const queryOptions = {
        singleView: this.documentRenderOptions.singleView || false,
        landscape: this.documentRenderOptions.landscape || false,
        useSignedAttachment: true
      };
      const query = Object.keys(queryOptions)
        .map(key => `${key}=${queryOptions[key]}`)
        .join('&');
      return config.renderURL + `/pdf/documents/${this.documentViewId}?${query}`;
    },

    checkedIds() {
      return this.checkedList.map(document => document.documentId);
    },

    selectedDocument() {
      if (this.checkedList.length === 1) {
        return this.checkedList[0];
      }
    },

    renderParams() {
      return [{ name: 'documentIds', value: JSON.stringify(this.checkedIds) }];
    },

    createPackPermissions() {
      return this.checkedIds.map(id => `DOCUMENT_PACK.ADD:document:${id}`);
    },

    ...mapState({
      list: state => state.documentIndex.list,
      count: state => state.documentIndex.count,

      status: state => state.status.data,

      settingTabs(state) {
        return this.getSettingTabs(state, this.$route);
      },

      columns: state => {
        if (state.setting && state.setting.settings && state.setting.settings[PublicSettings.COLUMNS]) {
          return JSON.parse(state.setting.settings[PublicSettings.COLUMNS].value);
        }
      },

      documentViewId: state => state.setting.settings[PublicSettings.DOCUMENT_VIEW_ID]?.value,

      documentRenderOptions: state =>
        (state.setting.settings[PublicSettings.DOCUMENT_RENDER_OPTIONS] &&
          JSON.parse(state.setting.settings[PublicSettings.DOCUMENT_RENDER_OPTIONS].value)) ||
        {},

      filters: state => state.setting.settings[PublicSettings.FILTERS]?.value || [],

      views: state => state.view.list,

      viewsToDownload: state => state.document.attachment.avaialbleViews,

      showCreatePack: state => state.setting.settings[PublicSettings.PACK_CREATE_SHOW]?.value === 'true',

      tableButtons(state) {
        if (state.setting.settings && state.setting.settings[PublicSettings.PROCEDURE_TABLE_BUTTONS]) {
          return this.singleRowActions.concat(
            JSON.parse(state.setting.settings[PublicSettings.PROCEDURE_TABLE_BUTTONS].value)
          );
        } else {
          return this.singleRowActions;
        }
      },

      getPdfText: state => state.setting.settings[PublicSettings.PROCEDURE_GET_PDF_BUTTON_TEXT]?.value
    })
  },

  methods: {
    // @vuese
    // Znalezienie zakładek
    getSettingTabs(state, route) {
      const currentTab = state.procedure.tabs.find(tab => tab.id === tryParseInt(route.query.tabId));

      if (currentTab) {
        return currentTab.children;
      }
    },

    // @vuese
    // Przenosi do szczegółów wniosku
    goToDetails(document) {
      this.$router.push({
        name: 'documentDetails',
        params: { documentId: document.documentId, procedureId: document.procedureId },
        query: this.$route.query
      });
    },

    // @vuese
    // Otwiera modal wyboru statusu dla listy dokumentów
    openMultipleStatusPicker() {
      this.getStatuses(this.$store.state.procedure.data.templateId)
        .then(() => {
          this.showStatusPicker = true;
        })
        .catch(err => this.notifyError(Errors.STATUS_CANNOT_LIST));
    },

    // @vuese
    // Zapisuje statusy dokumentów
    saveStatuses(data) {
      data.ids = this.checkedList.map(listItem => listItem.documentId);
      this.$store
        .dispatch('document/setStatuses', data)
        .then(() => {
          this.notifySuccess(Successes.DOCUMENT_SET_STATUSES);
          this.updateList(this.$route, this.$route, () => void 0);
        })
        .catch(err => this.notifyError(Errors.DOCUMENT_CANNOT_SET_STATUSES));
    },

    // @vuese
    // Otwiera modal generowania aktu prawnego
    openGenerateModal() {
      this.showGenerateModal = true;
    },

    // @vuese
    // zamyka modal generowania aktu prawnego
    closeGenerateModal() {
      this.showGenerateModal = false;
    },

    // @vuese
    // Otwiera modal pobierania aktów prawnych
    openDownloadModal() {
      return this.$store
        .dispatch('document/attachment/getAvailableViews', { documentIds: this.checkedIds })
        .then(() => (this.showDownloadModal = true));
    },

    // @vuese
    // zamyka modal pobierania aktów prawnych
    closeDownloadModal() {
      this.showDownloadModal = false;
    },

    // @vuese
    // Otwiera modal generowania CSV
    openCsvModal() {
      this.showCsvModal = true;
    },

    // @vuese
    // Zamyka modal generowania CSV
    closeCsvModal() {
      this.showCsvModal = false;
    },

    // @vuese
    // Otwiera modal tworzenia paczki dokumentów
    openCreatePackModal() {
      this.showCreatePackModal = true;
    },

    // @vuese
    // Zamyka modal tworzenia paczki dokumentów
    closeCreatePackModal() {
      this.showCreatePackModal = false;
    },

    // @vuese
    // Wywołuje request użycia przycisku
    useButton(actionData, data) {
      return this.$store
        .dispatch('button/useMultiple', {
          id: actionData.action.id,
          entityIds: this.checkedList.map(item => item.documentId.toString()),
          entityType: StageType.DOCUMENT,
          data
        })
        .then(() => {
          this.notifySuccess(Successes.ACTION_SUCCESS);
          this.updateList(this.$route, this.$route, () => void 0);
        })
        .catch(err => this.notifyError(Errors.ACTION_FAILED));
    },

    // @vuese
    // Wykonuje akcję zakończenia oceny
    finishAssessment(data) {
      this.useSingleButton(data);
    },

    // @vuese
    // Wywołuje request użycia przycisku
    useSingleButton(actionData) {
      return this.$store
        .dispatch('button/use', {
          id: actionData.action.id,
          entityId: actionData.object.id.toString(),
          entityType: StageType.DOCUMENT
        })
        .then(() => {
          this.notifySuccess(Successes.ACTION_SUCCESS);
          getDocumentListData(this.$route);
        })
        .catch(err =>
          this.notifyError(err.graphQLErrors ? err.graphQLErrors[0].message : Errors.ERROR_TRY_AGAIN_LATER)
        );
    },

    // @vuese
    // Tworzy paczkę dokumentów
    createPack(structureNodeId) {
      this.$store
        .dispatch('documentPack/add', {
          documentIds: this.checkedIds,
          procedureId: tryParseInt(this.procedureId),
          structureNodeId
        })
        .then(() => {
          this.notifySuccess(Successes.DOCUMENT_PACK_ADDED);
          this.updateList(this.$route, this.$route);
          this.closeCreatePackModal();
        })
        .catch(err => this.notifyError(Errors.DOCUMENT_PACK_CANNOT_ADD));
    },

    // @vuese
    // Aktualizacja listy
    updateList(to, from, next) {
      if (to.query.search && to.query.statusId !== from.query.statusId) {
        return next({ ...to, query: { ...to.query, search: undefined } });
      }
      getDocumentListData(to)
        .then(() => next && next())
        .catch(err => this.notifyError(Errors.DOCUMENT_CANNOT_LIST));
    }
  }
};
</script>
